import ComponentWithSocket from '../../lib/component';
import { Component } from 'vue-property-decorator';

import template from './verify-phone.vue';

import * as Store from './../../store';

import {
    PhoneValidation,
    PHONE_FORMAT_PREFIXED,
} from '../../utils/phone-validation';
import { getVikartSiteURL } from '../../services';

const CODE_LENGTH = 6;

// Intoarcerea pe site-ul public; cheie, nu URL, ca sa nu poata fi folosit ca redirect deschis
const RETURN_TARGETS: { [key: string]: () => string } = {
    vikart: () => getVikartSiteURL() + '/account/settings',
};

// Raspunsul la auth.phone.code; erorile de validare vin ca text simplu
interface PhoneCodeResponse {
    message?: string;
    phoneMasked?: string;
    retryAfter?: number;
    expiresIn?: number;
}

// Numarul nou, validat deja pe pagina de profil care a trimis userul aici
interface NewPhone {
    prefix: string;
    phone: string;
    phoneCountry: string;
}

interface VerifyPhoneData {
    // confirm = numarul curent (pasul de dupa inregistrare), change = numar nou
    mode: string;
    returnTo: string;
    // Licitatia de unde a venit userul (?auction=), ca dupa confirmare sa se intoarca la ea
    auctionId: string;
    newPhone: NewPhone | null;
    phoneMasked: string;
    code: string;
    retryAfter: number;
    error: string;
    sending: boolean;
    verifying: boolean;
}

@Component({
    mixins: [template],
})
export default class VerifyPhone extends ComponentWithSocket {
    data: VerifyPhoneData = {
        mode: 'confirm',
        returnTo: '',
        auctionId: '',
        newPhone: null,
        phoneMasked: '',
        code: '',
        retryAfter: 0,
        error: '',
        sending: false,
        verifying: false,
    };

    private countdown: any = null;

    // Ascultarea SMS-ului prin WebOTP (Chrome pe Android), oprita la iesirea din pagina
    private smsListener: any = null;

    beforeRouteLeave(to: any, from: any, next: any) {
        this.stopCountdown();
        this.stopSmsListener();
        this._beforeLeave(to, from);
        next();
    }

    beforeDestroy() {
        this.stopCountdown();
        this.stopSmsListener();
    }

    mounted() {
        Store.commitLoaderVisibility(this.$store, false);

        const query = this.$route.query;

        this.data.mode = query.mode === 'change' ? 'change' : 'confirm';
        this.data.returnTo = RETURN_TARGETS[String(query.return)]
            ? String(query.return)
            : '';
        this.data.auctionId = query.auction
            ? String(query.auction)
            : this.$store.state.currentAuctionID || '';

        this.wsListener.onPost(
            'auth.phone.code',
            (success: boolean, data: PhoneCodeResponse | string) =>
                this.onCodeSent(success, data),
        );

        this.wsListener.onPost(
            'auth.phone.verify',
            (success: boolean, data: any) => this.onVerified(success, data),
        );

        if (this.data.mode === 'change') {
            if (!query.prefix || !query.phone) {
                this.goNext();
                return;
            }

            this.data.newPhone = {
                prefix: String(query.prefix),
                phone: String(query.phone),
                phoneCountry: String(query.phoneCountry || ''),
            };
        } else {
            const user = Store.readLoggedInUser(this.$store);

            // Pasul e doar pentru conturile care il au de facut
            if (!user || !user.phoneConfirmationPending) {
                this.goNext();
                return;
            }
        }

        this.requestCode();
    }

    get canResend(): boolean {
        return !this.data.sending && this.data.retryAfter <= 0;
    }

    // Fara numar nou, codul pleaca la numarul curent al contului; numarul nou se salveaza
    // abia dupa confirmare (validarea finala o face API-ul)
    requestCode() {
        if (!this.canResend) return;

        // A doua verificare: numarul nou vine din URL, deci poate fi modificat de mana.
        // Un numar invalid nu ajunge la API si nu consuma din limita de SMS-uri
        const phoneError =
            this.data.newPhone && PhoneValidation.validate(this.data.newPhone);
        if (phoneError) {
            this.data.error = phoneError;
            return;
        }

        this.data.sending = true;
        this.data.error = '';

        this.wsListener.sendPost(
            'auth.phone.code',
            this.data.newPhone
                ? { ...this.data.newPhone, phoneFormat: PHONE_FORMAT_PREFIXED }
                : {},
        );
    }

    onCodeSent(success: boolean, data: PhoneCodeResponse | string) {
        const response: PhoneCodeResponse =
            typeof data === 'string' ? { message: data } : data || {};

        this.data.sending = false;

        if (response.retryAfter) {
            this.startCountdown(response.retryAfter);
        }

        if (response.phoneMasked) {
            this.data.phoneMasked = response.phoneMasked;
        }

        if (!success) {
            this.data.error = response.message || 'Nu am putut trimite codul.';
            return;
        }

        this.data.code = '';
        this.focusCode();
        this.listenForSmsCode();
    }

    onCodeInput() {
        this.data.code = this.data.code.replace(/\D/g, '').slice(0, CODE_LENGTH);

        if (this.data.code.length === CODE_LENGTH) {
            this.verifyCode();
        }
    }

    verifyCode() {
        if (this.data.verifying || this.data.code.length !== CODE_LENGTH) return;

        this.data.verifying = true;
        this.data.error = '';

        this.wsListener.sendPost('auth.phone.verify', { code: this.data.code });
    }

    onVerified(success: boolean, data: any) {
        this.data.verifying = false;

        if (!success) {
            this.data.error =
                (data && data.message) || 'Codul nu a putut fi verificat.';
            this.data.code = '';
            this.focusCode();
            return;
        }

        this.stopSmsListener();

        // Raspunsul aduce userul actualizat; tokenul sesiunii ramane cel salvat
        const user = { ...Store.readLoggedInUser(this.$store), ...data };

        this.wsListener.setAuthState(true, user);
        Store.commitLoggedInUser(this.$store, user);

        this.goNext(true);
    }

    // Dupa confirmare: inapoi pe site, la profil, la licitatia de unde a venit sau la
    // verificarea cu buletin
    goNext(confirmed: boolean = false) {
        if (this.data.returnTo) {
            window.location.href =
                RETURN_TARGETS[this.data.returnTo]() +
                (confirmed ? '?phoneConfirmed=1' : '');
            return;
        }

        if (!this.$root.$options || !this.$root.$options.router) return;

        if (this.data.mode === 'change') {
            this.$root.$options.router.push('/account');
        } else if (this.data.auctionId) {
            this.$root.$options.router.push('/auction/' + this.data.auctionId);
        } else {
            this.$root.$options.router.push('/verify');
        }
    }

    private focusCode() {
        this.$nextTick(() => {
            const input: any = this.$refs.codeInput;
            if (input) input.focus();
        });
    }

    /**
     * WebOTP: Chrome pe Android citeste codul din SMS (ultima linie, "@app.vikart.ro #123456")
     * dupa ce userul permite, il completeaza si il trimite. Merge doar pe domeniul din SMS;
     * pe iPhone codul vine din autocomplete="one-time-code", aici nu se intampla nimic.
     */
    private listenForSmsCode() {
        const nav: any = navigator;

        if (!('OTPCredential' in window) || !nav.credentials) return;

        this.stopSmsListener();
        this.smsListener = new AbortController();

        nav.credentials
            .get({ otp: { transport: ['sms'] }, signal: this.smsListener.signal })
            .then((otp: any) => {
                if (otp && otp.code && !this.data.verifying) {
                    this.data.code = String(otp.code);
                    this.onCodeInput();
                }
            })
            // Refuzat de user, oprit la iesire sau SMS-ul n-a venit: se tasteaza codul
            .catch(() => {});
    }

    private stopSmsListener() {
        if (this.smsListener) {
            this.smsListener.abort();
            this.smsListener = null;
        }
    }

    private startCountdown(seconds: number) {
        this.stopCountdown();
        this.data.retryAfter = seconds;

        this.countdown = setInterval(() => {
            this.data.retryAfter -= 1;

            if (this.data.retryAfter <= 0) {
                this.stopCountdown();
            }
        }, 1000);
    }

    private stopCountdown() {
        if (this.countdown) {
            clearInterval(this.countdown);
            this.countdown = null;
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/views/verify-phone/index.ts